import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row, Select, Spin, message } from 'antd';
import { withRouter } from 'react-router-dom';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import { MONTH_FORMAT } from 'utils/constants';
import { listCalendarInventory, listServicePackages } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import OccupancyMonthGrid from './OccupancyMonthGrid';
import { ALL_PROPERTIES, readPageFilters, toMonthDate, useSyncPageFilters } from './pageFilters';
import { findPackageById, unitsForPackage } from './reportingPackageFilter';

const normalizeId = id => (id && id._id ? String(id._id) : String(id));

const propertyIdOfUnit = unit => {
  const property = unit && unit.roomType && unit.roomType.property;
  if (!property) return '';
  if (typeof property === 'object') return String(property._id || '');
  return String(property);
};

const CalendarPage = ({ location, history }) => {
  const { hostId, hostsLoading } = useOwnerReportingHost();
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [filterPropertyId, setFilterPropertyId] = useState(
    () => readPageFilters(location && location.search).propertyId || ALL_PROPERTIES
  );
  const [servicePackageId, setServicePackageId] = useState(() => readPageFilters(location && location.search).servicePackageId);
  const [unitId, setUnitId] = useState(() => readPageFilters(location && location.search).unitId);
  const [monthDate, setMonthDate] = useState(() => readPageFilters(location && location.search).monthDate);
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState(null);

  const year = monthDate.year();
  const month = monthDate.month() + 1;

  const packagesForProperty = useMemo(() => {
    const list = packages || [];
    if (!filterPropertyId || filterPropertyId === ALL_PROPERTIES) return list;
    const unitIds = new Set(
      (hostUnits || [])
        .filter(u => String(propertyIdOfUnit(u)) === String(filterPropertyId))
        .map(u => String(u._id))
    );
    if (!unitIds.size) return [];
    return list.filter(pkg => (pkg.units || []).some(uid => unitIds.has(normalizeId(uid))));
  }, [packages, hostUnits, filterPropertyId]);

  const selectedPackage = useMemo(() => findPackageById(packagesForProperty, servicePackageId), [
    packagesForProperty,
    servicePackageId,
  ]);

  const unitOptions = useMemo(() => {
    if (!selectedPackage) return [];
    let list = unitsForPackage(hostUnits, selectedPackage);
    if (filterPropertyId && filterPropertyId !== ALL_PROPERTIES) {
      list = list.filter(u => String(propertyIdOfUnit(u)) === String(filterPropertyId));
    }
    return list;
  }, [hostUnits, selectedPackage, filterPropertyId]);

  const selectedUnit = useMemo(
    () => (unitId ? unitOptions.find(u => String(u._id) === String(unitId)) : null),
    [unitOptions, unitId]
  );
  const hideOccupancyBefore =
    (selectedUnit && selectedUnit.contractPeriod && selectedUnit.contractPeriod.start) || null;

  useEffect(() => {
    if (!hostId) {
      if (hostsLoading) return undefined;
      setPackages([]);
      setProperties([]);
      setHostUnits([]);
      setFilterPropertyId(undefined);
      setServicePackageId(undefined);
      setUnitId(undefined);
      setInventory(null);
      setPackagesLoading(false);
      setPropertiesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPackages([]);
    setProperties([]);
    setHostUnits([]);
    setPackagesLoading(true);
    setPropertiesLoading(true);
    Promise.all([listServicePackages(hostId), loadHostProperties(hostId), loadHostUnits(hostId)])
      .then(results => {
        if (cancelled) return;
        const list = Array.isArray(results[0]) ? results[0] : [];
        const props = results[1] || [];
        setPackages(list);
        setProperties(props);
        setHostUnits(results[2] || []);
      })
      .catch(() => {
        if (!cancelled) {
          message.error('Failed to load calendar filters');
          setPackages([]);
          setProperties([]);
          setHostUnits([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPackagesLoading(false);
          setPropertiesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hostId, hostsLoading]);

  useEffect(() => {
    if (!hostId || packagesLoading) return;
    setServicePackageId(prev => {
      if (prev && packagesForProperty.some(p => String(p._id) === String(prev))) return prev;
      return packagesForProperty[0] ? String(packagesForProperty[0]._id) : undefined;
    });
  }, [hostId, packagesLoading, packagesForProperty]);

  useEffect(() => {
    if (!hostId || propertiesLoading) return;
    if (!filterPropertyId || filterPropertyId === ALL_PROPERTIES) return;
    if (properties.some(p => String(p._id) === String(filterPropertyId))) return;
    setFilterPropertyId(ALL_PROPERTIES);
  }, [hostId, propertiesLoading, properties, filterPropertyId]);

  useEffect(() => {
    if (!hostId || packagesLoading || !selectedPackage) return;
    setUnitId(prev => {
      if (!prev) return undefined;
      if (unitOptions.some(u => String(u._id) === String(prev))) return prev;
      return undefined;
    });
  }, [hostId, packagesLoading, selectedPackage, unitOptions]);

  useSyncPageFilters(history, location, {
    property: filterPropertyId,
    servicePackage: servicePackageId,
    unit: unitId,
    date: monthDate.format(MONTH_FORMAT),
  });

  useEffect(() => {
    if (!hostId || !servicePackageId) {
      setInventory(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    listCalendarInventory(hostId, { servicePackageId, year, month })
      .then(data => {
        if (!cancelled) setInventory(data);
      })
      .catch(err => {
        if (!cancelled) {
          message.error((err && err.message) || 'Failed to load calendar inventory');
          setInventory(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hostId, servicePackageId, year, month]);

  return (
    <Card title="Owner Reporting — Calendar">
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Filter Property" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                value={filterPropertyId}
                onChange={val => setFilterPropertyId(val ? String(val) : ALL_PROPERTIES)}
                disabled={!hostId}
                loading={propertiesLoading}
              >
                <Select.Option value={ALL_PROPERTIES}>All</Select.Option>
                {properties.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Service package" required style={{ marginBottom: 0 }}>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={packagesLoading ? 'Loading packages...' : 'Select service package'}
                value={servicePackageId}
                onChange={val => setServicePackageId(val ? String(val) : undefined)}
                disabled={!hostId || packagesLoading}
                loading={packagesLoading}
              >
                {packagesForProperty.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name || String(p._id)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Unit" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={
                  packagesLoading
                    ? 'Loading units...'
                    : servicePackageId
                    ? 'All units in package'
                    : 'Select service package first'
                }
                value={unitId}
                onChange={val => setUnitId(val ? String(val) : undefined)}
                disabled={!hostId || packagesLoading || !servicePackageId}
                loading={packagesLoading}
                notFoundContent={packagesLoading ? 'Loading...' : 'No units for this package'}
              >
                {unitOptions.map(u => (
                  <Select.Option key={String(u._id)} value={String(u._id)}>
                    {u.name || String(u._id)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Month" style={{ marginBottom: 0 }}>
              <MonthPicker defaultValue={monthDate} onChange={val => setMonthDate(toMonthDate(val))} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {!hostId && <p style={{ color: 'rgba(0,0,0,0.45)' }}>Select a host to view calendar inventory.</p>}
      {hostId && !packagesLoading && packages.length === 0 && (
        <p style={{ color: 'rgba(0,0,0,0.45)' }}>No service packages found for this host.</p>
      )}
      {hostId && !packagesLoading && packages.length > 0 && packagesForProperty.length === 0 && (
        <p style={{ color: 'rgba(0,0,0,0.45)' }}>No service packages found for this property.</p>
      )}
      {hostId && packages.length > 0 && !servicePackageId && (
        <p style={{ color: 'rgba(0,0,0,0.45)' }}>Select a service package to view availability.</p>
      )}

      {hostId && servicePackageId && (
        <Spin spinning={loading}>
          <OccupancyMonthGrid
            year={year}
            month={month}
            inventory={inventory}
            hideOccupancyBefore={hideOccupancyBefore}
          />
        </Spin>
      )}
    </Card>
  );
};

export default withRouter(CalendarPage);
